import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { createApiQueryHook } from '../../common/queryClient/createApiQueryHook';
import { SessionUserType, UserPermissionType } from '../../user/types';
import { apiClient } from '../../common/ApiClient';
import { AuthUrl } from './AuthUrl';

export interface UserInfo {
  user: SessionUserType;
  userPermissions: UserPermissionType[];
}

const userInfo = () => apiClient.get<UserInfo>(AuthUrl.USER_INFO);

const queryKey = ['userInfo'] as const;

export const useUserInfoQuery = createApiQueryHook(queryKey, userInfo, {
  /**
   * User info is tied to session user so there should be no need to refetch this data during one session.
   * If user logs out, session ends and this query data should be cleared.
   * @atlas-frontend/sdk/RequireAuth component takes care of that by calling `refetch` upon logout which
   * results in error 401 UNAUTHORIZED with empty body = empty query data.
   */
  staleTime: Infinity,
  gcTime: Infinity,
  /**
   * From React Query docs: If set to "always", the query will always refetch on window focus (except when staleTime: 'static' is used).
   * We want the combination of staleTime: Infinity and refetchOnWindowFocus: 'always' so that user-info along with the CSRF token header
   * is always up-to-date when user returns to the app after being away and the session might have changed.
   */
  refetchOnWindowFocus: 'always',
});

export const useInvalidateUserInfoQueries = () => {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey, exact: false }),
    [queryClient],
  );
};
